<template>
  <v-card elevation="0" class="mainShareCard">
    <v-container v-if="!permissionPending">
      <v-layout>
        <v-flex>
          <v-card class="ownerCard" :key="coOwnersKey">
            <v-list two-line>
              <v-subheader>Current Owner(s)</v-subheader>
              <v-list-tile
              v-for="user in classCoOwners"
              :key="user.teacher_id"
              avatar
              >
              <v-list-tile-avatar>
                <img v-if="user.userInfo.picture != null" :src="user.userInfo.picture" alt="avatar">
                <v-icon v-else large>account_circle</v-icon>
              </v-list-tile-avatar>
              <v-list-tile-content class="ownerList">
                <v-list-tile-title v-dompurify-html="user.userInfo.email"></v-list-tile-title>
                <v-list-tile-sub-title>{{ shareAccessRole(user.container_role) }}</v-list-tile-sub-title>
              </v-list-tile-content>
              <v-list-tile-action class="actionSelect">
                <v-flex xs6 class="ownerAction">
                  <v-select
                  v-if="user.container_role !== 1"
                  :items="newAccessLevels"
                  v-model="user.container_role"
                  label="Select Access"
                  :disabled="!isClassOwner"
                  @change="setNewAccessLevel(user)"
                  ></v-select>
                </v-flex>
              </v-list-tile-action>
              </v-list-tile>
            </v-list>
          </v-card>
        </v-flex>
      </v-layout>
      <v-layout
        v-if="isClassOwner"
        class="mt-3"
        align-center>
        <v-flex xs10>
          <v-autocomplete
          v-model="addEmailList"
          hide-no-data
          hide-selected
          cache-items
          :items="matchingSchoolEmails"
          :label="accessLabel"
          item-text="email"
          item-value="id"
          :search-input.sync="emailSearchString"
          multiple
          chips
          deletable-chips
          :loading="searchingForEmailMatch"
          return-object
          :key="coOwnersKey"
          @input="clearEmailSearchString"
          small-chips
          >
          </v-autocomplete>
        </v-flex>
        <v-spacer></v-spacer>
        <v-flex>
          <v-menu
          >
            <template v-slot:activator="{ on }">
              <v-btn
                color="white"
                v-on="on"
                class="mr-0"
              >
                <div v-if="shareAccess === 'Instructor'">
                  <v-icon color="primary">create</v-icon><v-icon color="primary">arrow_drop_down</v-icon>
                </div>
                <div v-else>
                  <v-icon color="primary">remove_red_eye</v-icon><v-icon color="primary">arrow_drop_down</v-icon>
                </div>
              </v-btn>
            </template>
            <v-list>
              <v-list-tile
                v-for="(role, index) in roles"
                :key="index"
                @click="setShareAccess(role)"
              >
                <v-list-tile-title>{{ role }}</v-list-tile-title>
              </v-list-tile>
            </v-list>
          </v-menu>
        </v-flex>
      </v-layout>
      <v-layout
        v-if="isClassOwner">
        <v-flex>
          <v-btn
          color="primary"
          class="ma-0"
          @click="submitAddClassCoOwners"
          :disabled="addEmailList.length === 0 || searchingForEmailMatch"
          >
            Share Access
          </v-btn>
        </v-flex>
        <v-flex>
          <v-tooltip top>
            <template v-slot:activator="{ on }">
              <v-switch
              v-on="on"
              class="justify-end"
              v-model="localOverwriteAccess"
              label="Update user bulb permissions.">
              </v-switch>
            </template>
            <span style="color:#fff">This will update bulb sharing permissions to match class permissions.</span>
          </v-tooltip>
        </v-flex>
      </v-layout>
    </v-container>
    <v-container v-else>
      <p>Clicking transfer class will transfer ownership of the class to another PlayPosit instructor. Once transferred, your role will update to an instructor. <a href="https://knowledge.playposit.com/article/255-class-bulb-permissions#instructor" target="_blank">Click here</a> for more information on the abilities of an instructor.</p>
      <p v-if="overwriteAccess">This action will also transfer ownership of all bulbs assigned to this section to another PlayPosit instructor. Once transferred, your role for these bulbs will update to a co-editor. <a href="https://knowledge.playposit.com/article/255-class-bulb-permissions#ownvsview" target="_blank">Click here</a> for more information on the abilities of a co-editor.</p>
    </v-container>
  </v-card>
</template>

<script>
'use strict';
import {
  mapState,
  mapGetters,
  mapMutations,
  mapActions
} from 'vuex';
// Validations from Vuelidate
import { validationMixin } from 'vuelidate';
import _ from 'lodash';

export default {
  name: 'coursePermission',
  components: {
  },
  mixins: [validationMixin],
  props: [
    'selectedClass',
    'schoolId',
    'overwriteAccess',
  ],
  data() {
    return {
      addEmailList: [],
      emailSearchString: null,
      emailMatchesSearched: false,
      matchingSchoolEmails: [],
      newAccessLevels: [
        {
          text: 'Instructor',
          value: 2,
        },
        {
          text: 'Teaching Assistant',
          value: 3,
        },
        {
          text: 'Remove Access',
          value: 0,
        },
        {
          text: 'Make Owner',
          value: 1,
        },
      ],
      roles: [
        'Instructor',
        'Teaching Assistant',
      ],
      shareAccess:'Instructor',
    };
  },
  computed: {
    ...mapState('instructors', [
      'classCoOwners',
      'coOwnersKey',
      'emailMatchesFromSchool',
      'searchingForEmailMatch',
      'permissionPending',
    ]),
    ...mapGetters([
      'instructorData',
      'userImg',
      'shareNodeApiPrefix'
    ]),
    accessLabel() {
      if (this.shareAccess === 'Instructor') return 'Enter email addresses to allow edit/share/view access.';
      return 'Enter email addresses to allow share/view access.';
    },
    isClassOwner() {
      return this.selectedClass.containerRole === 1;
    },
    shareAccessNumber() {
      if (this.shareAccess === 'Instructor') return 2;
      if (this.shareAccess === 'Teaching Assistant') return 3;
      return null;
    },
    localOverwriteAccess: {
      get() {
        return this.overwriteAccess;
      },
      set(overwriteAccess) {
        this.$emit('update:overwriteAccess', overwriteAccess);
      },
    },
  },
  methods: {
    ...mapMutations([
      'triggerSnackbar',
    ]),
    ...mapMutations('instructors', [
      'setPermissionPending',
      'setSelectedUser',
    ]),
    ...mapActions('instructors', [
      'addClassCoOwners',
    ]),
    clearEmailSearchString() {
      this.emailSearchString = '';
    },
    setShareAccess(role) {
      this.shareAccess = role;
    },
    shareAccessRole(role) {
      if (role === 0) return 'Remove Access';
      if (role === 1) return 'Owner';
      if (role === 2) return 'Instructor - can edit/share/view';
      if (role === 3) return 'Teaching Assistant - can share/view';
    },
    setNewAccessLevel (user) {
      if (user.container_role === 1) {
        this.setPermissionPending(true);
        this.setSelectedUser(user);
      } else {
        this.$store.dispatch('instructors/updateClassCoOwners', { teacherId: user.teacher_id, accessLevel: user.container_role, sectionId: this.selectedClass.id, overwriteAccess: this.localOverwriteAccess });
      }
    },
    formatSchoolEmails () {
      this.matchingSchoolEmails = [];
      this.alreadyAddedError = false;
        for (const match of this.emailMatchesFromSchool) {
          if (match.email !== this.instructorData.email && !_.find(this.classCoOwners, ['userInfo.email', match.email])) {
            this.matchingSchoolEmails.push(match);
          }
        }
    },
    submitAddClassCoOwners() {
      const addUserGroup = {
        sectionId: this.selectedClass.id,
        userEmails: this.addEmailList,
        accessLevel: this.shareAccessNumber,
        overwriteAccess: this.localOverwriteAccess,
      };
      this.addClassCoOwners(addUserGroup);
      this.addEmailList = [];
    },
  },
  watch:{
    'addEmailList' (n, o) {
      if (n.length > 3) {
        // 3 is the max number of co-owners that can be added at a time. - Pops the last name entered out and triggers snackbar.
        this.triggerSnackbar({text:"Only 3 co-owners can be added at a time.", color: 'warning', timeout: 5000});
        this.addEmailList.pop();
      }
    },
    'emailMatchesFromSchool'(n, o) {
      this.formatSchoolEmails();
    },
    'emailSearchString' (n, o) {
      // reset email matches search if input is less than 4 characters
      if (n.length < 4) this.emailMatchesSearched = false;
      // Trigger email matches search when input = 4 characters and we haven't searched yet
      if (n.length > 3 && !this.emailMatchesSearched) {
        this.emailMatchesSearched = true;
        this.$store.dispatch('instructors/searchForEmailMatches', n);
      }
    },
    permissionPending(newValue) {
      if (newValue) {
        this.$store.commit('instructors/resetClassCoOwners');
        this.$store.dispatch('instructors/lookForClassCoOwners', this.selectedClass.id);
      }
    },
  },
  mounted() {
    this.$store.dispatch('instructors/lookForClassCoOwners', this.selectedClass.id);
  },
  destroyed() {
    this.$store.commit('instructors/resetClassCoOwners');
  }
};
</script>

<style>
.ownerList {
  overflow: initial !important;
}

.no-class-container{
  width: 100%;
  height: 100px;
  display: flex;
  flex-flow: row;
  justify-content: space-around;
  align-items: center;
  padding: 0px 36px;
}

.notOwner {
  background: white;
  font-size: 16px;
  color: rgba(0, 0, 0, 0.64);
}

.padding-left {
  padding-left: 18px;
}

.actionSelect {
  max-width: 55% !important;
}

.ownerCard {
  max-height: 350px;
  overflow-y: auto;
}

.emailInput {
  width:100%;
}

.mainShareCard {
  min-height: 300px;
  background-color: #fff;
  overflow: auto;
}

</style>
